"use client";

import useSimulatedProgress from "@/hooks/useSimulatedProgress";

// Couleur de la barre de progression (prop `progress`) selon le type de bouton.
// Fonds pleins → nuance plus foncée du même ton, texte blanc conservé.
// Fonds clairs/neutres → orange de marque, texte blanc (bascule noir → blanc).
const progressFillMap = {
    default: { bg: "#B23C10", text: "#ffffff" },
    primary: { bg: "#B23C10", text: "#ffffff" },
    pop: { bg: "#B23C10", text: "#ffffff" },
    premium: { bg: "#B23C10", text: "#ffffff" },
    "premium-price": { bg: "#B23C10", text: "#ffffff" },
    excitation: { bg: "#B23C10", text: "#ffffff" },
    cta: { bg: "#1E40AF", text: "#ffffff" },
    success: { bg: "#15803D", text: "#ffffff" },
    warning: { bg: "#A16207", text: "#ffffff" },
    danger: { bg: "#DC2626", text: "#ffffff" },
    gold: { bg: "#8A6D0B", text: "#ffffff" },
    confidence: { bg: "#3C3489", text: "#ffffff" },
    white: { bg: "#E8511D", text: "#ffffff" },
    secondary: { bg: "#E8511D", text: "#ffffff" },
    google: { bg: "#E8511D", text: "#ffffff" },
    phantom: { bg: "#E8511D", text: "#ffffff" },
    light: { bg: "#E8511D", text: "#ffffff" },
    "light-purple": { bg: "#3C3489", text: "#ffffff" },
    "outline-pop": { bg: "#E8511D", text: "#ffffff" },
};

export default function Button(props) {
    // Barre de progression intégrée (façon ElevenLabs), opt-in via `progress`.
    // `progress` booléen → barre simulée pilotée par `isLoading`.
    // `progress` nombre 0..100 → barre déterminée (upload, traitement par étapes).
    const isProgress =
        props.progress !== undefined && props.progress !== false;
    const determinate = typeof props.progress === "number";
    const sim = useSimulatedProgress(
        !determinate && isProgress && !!props.isLoading,
    );

    let progressMounted = false;
    let progressPct = 0;
    let progressVisible = false;
    let barEnabled = false;
    if (isProgress) {
        if (determinate) {
            progressPct = Math.max(0, Math.min(100, props.progress));
            progressMounted = progressPct > 0;
            progressVisible = true;
            barEnabled = true;
        } else {
            progressMounted = sim.mounted;
            progressPct = sim.pct;
            progressVisible = sim.visible;
            barEnabled = sim.enabled;
        }
    }
    // showBar = barre animée active (false en reduced-motion).
    const showBar = isProgress && barEnabled;
    // Repli reduced-motion : remplissage plein statique tant que isLoading, au
    // lieu du spinner, pour rester cohérent avec la barre (mais sans mouvement).
    const staticFill =
        isProgress && !determinate && !!props.isLoading && !barEnabled;
    const renderOverlay = (showBar && progressMounted) || staticFill;
    const overlayPct = staticFill ? 100 : progressPct;
    const overlayVisible = staticFill ? true : progressVisible;
    const fillConfig = progressFillMap[props.type] || progressFillMap.default;
    const fill = {
        bg: props.progressColor || fillConfig.bg,
        text: props.progressTextColor || fillConfig.text,
    };

    let size;

    if (props.type && props.type === "premium") {
        size = "px-8 py-4 rounded-full";
    } else if (
        props.type &&
        (props.type === "premium-price" ||
            props.type === "premium-price-outline" ||
            props.type === "premium-price-disabled" ||
            props.type === "starter-price")
    ) {
        size = "px-8 py-4 rounded-full";
    } else if (props.type && props.type === "cta") {
        if (props.small) {
            size = "px-5 py-2 rounded-full";
        } else {
            size = "px-9 py-4 rounded-full";
        }
    } else {
        if (props.small) {
            size = "px-3 py-2 rounded-xl text-sm";
        } else if (props.large) {
            size = "px-5 py-2 rounded-xl";
        } else if (props.block) {
            size = "w-full py-4 rounded-xl";
        } else {
            size = "px-8 py-4 rounded-xl";
        }
    }

    let type;
    if (props.type && props.type === "light") {
        // light
        type =
            "border border-white bg-transparent text-white hover:bg-white hover:text-black";
    } else if (props.type && props.type === "white") {
        // white
        type = "bg-white text-black hover:bg-gray-300 hover:text-black";
    } else if (props.type && props.type === "success") {
        // success
        type = "bg-green-500 text-white hover:bg-green-400";
    } else if (props.type && props.type === "warning") {
        // warning
        type = "bg-yellow-500 text-white hover:bg-yellow-400";
    } else if (props.type && props.type === "danger") {
        // danger
        // type = "bg-red-500 text-white hover:bg-red-400";
        type = "bg-red-300 text-red-800 hover:bg-red-400 hover:text-white";
    } else if (props.type && props.type === "light-purple") {
        // light purple
        type =
            "border border-primary dark:border-primary-100 bg-transparent text-primary dark:text-primary-100 hover:bg-primary dark:hover:bg-primary-100 hover:text-white dark:hover:text-white";
    } else if (props.type && props.type === "gold") {
        // gold
        type = "bg-gold text-white hover:bg-gold-900";
    } else if (props.type && props.type === "light-gold") {
        // light gold
        type =
            "border border-gold bg-transparent text-gold hover:bg-gold hover:text-white";
    } else if (props.type && props.type === "oldgold") {
        // oldgold
        type = "bg-gold-900 text-white hover hover:bg-gold-900/30";
    } else if (props.type && props.type === "confidence") {
        // confidence
        type = "bg-confidence text-white hover:bg-confidence-100";
    } else if (props.type && props.type === "outline-confidence") {
        // outline-confidence
        type =
            "border border-confidence text-confidence bg-transparent hover:bg-confidence-100 hover:border-transparent hover:text-white";
    } else if (props.type && props.type === "excitation") {
        // excitation
        type = "bg-excitationOrange text-white hover:bg-excitationRed";
    } else if (props.type && props.type === "pop") {
        // pop
        type =
            "bg-primary text-white hover:brightness-95";
    } else if (props.type && props.type === "primary") {
        // primary — orange plein (système neutre + orange parcimonieux)
        type = "bg-primary text-white hover:brightness-95";
    } else if (props.type && props.type === "secondary") {
        // secondary — neutre sobre (façon Stripe), ombre légère
        type =
            "bg-white dark:bg-bgDark-840 border border-gray-200 dark:border-borderDark text-gray-700 dark:text-grayDark-200 hover:bg-gray-50 dark:hover:bg-bgDark-900/60 hover:border-gray-300 dark:hover:border-grayDark-600";
    } else if (props.type && props.type === "outline-pop") {
        // outline
        type =
            "border border-primary text-primary bg-transparent hover:bg-primary hover:text-white";
    } else if (props.type && props.type === "phantom") {
        // phantom
        type =
            "text-primary hover:text-primary-100 border border-gray-100 dark:border-borderDark hover:border-primary-100 dark:hover:border-primary-100";
    } else if (props.type && props.type === "premium") {
        // premium
        type =
            "bg-primary text-white hover:brightness-95";
    } else if (props.type && props.type === "premium-price") {
        // premium-price : orange plein (pas de dégradé rose)
        type = "bg-primary text-white hover:bg-primary/90";
    } else if (props.type && props.type === "premium-price-outline") {
        // premium-price-outline
        type =
            "border border-primary text-primary bg-transparent hover:bg-primary hover:text-white";
    } else if (props.type && props.type === "premium-price-disabled") {
        // premium-price-disabled
        type =
            "border border-gray-200 dark:border-grayDark-600 bg-gray-100 dark:bg-grayDark-700 text-gray-800 dark:text-grayDark-400";
    } else if (props.type && props.type === "starter-price") {
        // starter-price — grisé au maximum
        type =
            "bg-gray-100 dark:bg-grayDark-800 text-gray-400 dark:text-grayDark-500 border border-gray-200 dark:border-grayDark-700 hover:bg-gray-200 dark:hover:bg-grayDark-700";
    } else if (props.type && props.type === "cta") {
        // cta
        type = "bg-blue-500 text-white hover:bg-blue-400";
    } else if (props.type && props.type === "google") {
        // google
        size = "px-8 py-4 rounded-full w-full";
        type =
            "bg-white text-black hover:bg-primary/5 border dark:border-borderDark hover:text-black dark:bg-black dark:text-white dark:hover:bg-primary/10 dark:hover:text-white";
    } else if (props.type && props.type === "duo") {
        // duolingo style
        type =
            "bg-gradient-to-b from-[#58cc02] to-[#4CAF50] border-b-4 border-[#46a302] text-white font-bold shadow-lg transform transition-all duration-150 ease-in-out active:border-b-2 active:translate-y-1 focus:outline-hidden focus:ring-4 focus:ring-green-300 relative overflow-hidden";
    } else if (props.type && props.type === "duo-secondary") {
        // duolingo secondary style (like skip button)
        type =
            "bg-gray-100 border-b-4 border-gray-300 text-gray-600 font-bold shadow-md transform transition-all duration-150 ease-in-out active:border-b-2 active:translate-y-1 focus:outline-hidden focus:ring-4 focus:ring-gray-200 relative overflow-hidden";
    } else if (props.type && props.type === "outline-secondary") {
        // outline secondary
        type =
            "border border-gray-300 dark:border-grayDark-700 dark:bg-grayDark-800/80 hover:bg-gray-100 dark:hover:bg-grayDark-700 text-gray-600 dark:text-grayDark-400 font-bold shadow-md transform transition-all duration-150 ease-in-out relative overflow-hidden";
    } else if (props.type && props.type === "outline-secondary-dark") {
        // outline secondary dark
        type =
            "border border-grayDark-700 bg-grayDark-800/80 hover:bg-grayDark-700 text-gray-400 font-bold shadow-md transform transition-all duration-150 ease-in-out relative overflow-hidden";
    } else if (props.type && props.type === "disabled") {
        // disabled
        type =
            "bg-gray-100 dark:bg-grayDark-700 text-gray-800 dark:text-grayDark-400 border-gray-200 dark:border-grayDark-600";
    } else {
        // default
        type = "bg-primary text-white hover:bg-primary-300";
    }

    let disabled;
    if (props.isLoading || props.disabled) {
        if (props.type === "duo" || props.type === "duo-secondary") {
            // Override type for disabled duo buttons
            type =
                "bg-gray-300 border-b-4 border-gray-400 text-gray-500 cursor-not-allowed font-bold shadow-lg relative overflow-hidden";
            disabled = "cursor-not-allowed";
        } else {
            disabled = "cursor-not-allowed opacity-50";
        }
    } else {
        disabled = "cursor-pointer";
    }

    // Effet "touche 3D" : lèvre colorée + ombre douce au repos, enfoncement au
    // clic. Le style vit dans styles/globals.css (.btn-3d) ; la couleur de la
    // lèvre passe par la variable --lip. Pleins = lèvre colorée ; outline /
    // secondaires = lèvre grise discrète (fallback). Exclus : duo et désactivés.
    let press = "";
    let lipColor = null;
    const noPressTypes = [
        "duo",
        "duo-secondary",
        "outline-secondary",
        "outline-secondary-dark",
        "disabled",
        "premium-price-disabled",
        "starter-price",
        // Boutons outline transparents : pas d'effet 3D (la lèvre flotterait sous
        // du vide et rendrait mal). On les garde propres et plats.
        "light",
        "light-purple",
        "light-gold",
        "outline-pop",
        "outline-confidence",
        "premium-price-outline",
        "phantom",
    ];
    const solidLip = {
        primary: "#A8360F",
        "premium-price": "#A8360F",
        premium: "#A8360F",
        excitation: "#A8360F",
        pop: "#A8360F",
        cta: "#1E40AF",
        success: "#15803D",
        warning: "#A16207",
        danger: "#991B1B",
        gold: "#8A6D0B",
        confidence: "#3C3489",
    };
    if (
        !props.isLoading &&
        !props.disabled &&
        !noPressTypes.includes(props.type)
    ) {
        press = "btn-3d";
        lipColor = solidLip[props.type] || (props.type ? null : "#A8360F");
    }

    return (
        <button
            className={`button group ${size} ${type} ${press} ${
                props.className || ""
            } ${disabled} ${isProgress ? "overflow-hidden" : ""}`}
            onClick={props.onClick}
            ref={props.ref}
            disabled={props.disabled || props.isLoading}
            // `type` (prop) est une variante visuelle, pas l'attribut HTML :
            // sans `htmlType`, un Button dans un <form> est submit par défaut
            type={
                props.htmlType ||
                (props.type === "submit" ? "submit" : undefined)
            }
            style={lipColor ? { "--lip": lipColor } : undefined}
        >
            <div
                className={`flex items-center justify-center ${
                    props.childrenClassName || ""
                } ${isProgress ? "relative z-[1]" : ""}`}
            >
                {/* Content wrapper for duo buttons */}
                {props.type === "duo" || props.type === "duo-secondary" ? (
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        {props.isLoading && (
                            <svg
                                className="animate-spin h-5 w-5 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        )}
                        {props.children}
                    </span>
                ) : (
                    <>
                        {props.isLoading && !showBar && !staticFill && (
                            <svg
                                className="animate-spin h-4 w-4 mr-2"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        )}
                        {props.children}
                        {props.type && props.type === "premium" && !props.isLoading && (
                            <div>
                                <div class="relative flex h-4 w-4 pl-1">
                                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                    <span class="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Barre de progression : copie du contenu, colorée et découpée au
                bord exact de la barre par clip-path → le texte bascule de
                couleur au pixel près là où la barre le traverse. */}
            {renderOverlay && (
                <span
                    aria-hidden="true"
                    className={`absolute inset-0 z-[2] flex items-center justify-center ${
                        props.childrenClassName || ""
                    }`}
                    style={{
                        background: fill.bg,
                        color: fill.text,
                        opacity: overlayVisible ? 1 : 0,
                        transition: staticFill ? "none" : "opacity 0.35s ease",
                        clipPath: `inset(0 ${100 - overlayPct}% 0 0)`,
                        WebkitClipPath: `inset(0 ${100 - overlayPct}% 0 0)`,
                    }}
                >
                    {props.children}
                </span>
            )}

            {/* Shine effect for duo buttons - only when enabled */}
            {(props.type === "duo" || props.type === "duo-secondary") &&
                !props.isLoading &&
                !props.disabled && (
                    <div
                        className={`absolute inset-0 bg-gradient-to-r from-transparent ${
                            props.type === "duo-secondary"
                                ? "via-white/30"
                                : "via-white/20"
                        } to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl`}
                    ></div>
                )}
        </button>
    );
}
